import React, { FC, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';

import { Badge, Button, Link, Paper, TablePagination, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import MaterialTable from 'material-table';

import {
	Lock,
	Security,
	Snooze,
	SportsBasketball,
	SportsCricket,
	SportsFootball,
	SportsGolf,
	SportsMotorsports,
	SportsSoccer
} from '@material-ui/icons';

import type { RootState } from '../../store/store';
import { name as gameFacetSelector, setSorting } from '../../store/reducers/gameFacet';
import { useLanguageContext } from '../languages';
import { getLocalTimezone } from '../../utils/dateTransform';
import { Airing, Game, Highlight, USE_DRM } from '../../utils/types';
import StatusChip, { statusChipStyles } from '../common/StatusChip';
import ActionMenu from './ActionMenu';
import CopyText from '../common/CopyText';
import commonMessages from '../common/messages';

const useStyles = makeStyles(() => ({
	channelList: {
		listStyleType: 'none',
		padding: 0,
		margin: 0
	},
	channelListItem: {
		marginBottom: 3
	}
}));

const StyledBadge1 = withStyles((theme: Theme) =>
	createStyles({
		badge: {
			right: 3,
			top: 7,
			'& svg': {
				fontSize: '0.9rem'
			}
		}
	})
)(Badge);
const StyledBadge2 = withStyles((theme: Theme) =>
	createStyles({
		badge: {
			right: 3,
			top: 24,
			'& svg': {
				fontSize: '0.9rem'
			}
		}
	})
)(Badge);

const GamesList: FC<GamesListProps> = (props) => {
	const { games, pageSize, page, count, onPageChange, refetch, useDrm } = props;

	const classes = useStyles();
	const statusChipClasses = statusChipStyles();
	const dispatch = useDispatch();

	// Search facets come from the gameFacetSelector in the redux store
	const { sorting } = useSelector((state: RootState) => state[gameFacetSelector]);
	const { orderBy, orderDirection } = sorting;

	const intl = useIntl();
	const { tableLocalization } = useLanguageContext();

	const rowStyle = (rowData: Game): CSSProperties => {
		const styling: CSSProperties = {
			overflowWrap: 'break-word'
		};

		if (rowData.airings && rowData.airings.items.length > 0) {
			styling.backgroundColor = '#f5f5f5';
		}

		return styling;
	};

	const onOrderChange = (orderBy: number, orderDirection: 'asc' | 'desc'): void => {
		dispatch(setSorting({ orderBy, orderDirection }));
	};

	const renderSportIcon = (sport: string): JSX.Element => {
		let sportIcon = <SportsSoccer fontSize='large' color='action' />;
		switch (sport) {
			case 'AmericanFootball':
				sportIcon = <SportsFootball fontSize='large' color='action' />;
				break;
			case 'Basketball':
				sportIcon = <SportsBasketball fontSize='large' color='action' />;
				break;
			case 'Cricket':
				sportIcon = <SportsCricket fontSize='large' color='action' />;
				break;
			case 'Golf':
				sportIcon = <SportsGolf fontSize='large' color='action' />;
				break;
			case 'F1':
				sportIcon = <SportsMotorsports fontSize='large' color='action' />;
				break;
		}
		return (
			<div style={{ display: 'inline-block', float: 'left', minHeight: '2rem', marginRight: '0.8rem', marginTop: '0.5rem' }}>
				<Tooltip title={sport}>{sportIcon}</Tooltip>
			</div>
		);
	};

	const renderStatusChip = (airing: Airing): JSX.Element => {
		const { alternateIds, channel, drm, id, status } = airing;

		const isProtected = typeof alternateIds?.items.find((altId): boolean => altId.namespace === 'PROTECTED_AIRING') !== 'undefined';
		const isDrmActive = [USE_DRM.YES, USE_DRM.BOTH].includes(useDrm);
		const isDrm = !!drm;

		return (
			<li key={id} className={classes.channelListItem}>
				<li key={`airing_${id}`} className={classes.channelListItem}>
					{isProtected && isDrmActive && isDrm && (
						<StyledBadge1
							overlap='rectangular'
							badgeContent={
								<Tooltip title='DRM Protected'>
									<Security color='action' />
								</Tooltip>
							}>
							<StyledBadge2
								overlap='rectangular'
								badgeContent={
									<Tooltip title='Protected Airing'>
										<Lock color='action' />
									</Tooltip>
								}>
								<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
							</StyledBadge2>
						</StyledBadge1>
					)}
					{isProtected && (!isDrmActive || !isDrm) && (
						<StyledBadge1
							badgeContent={
								<Tooltip title='Protected Airing'>
									<Lock color='action' />
								</Tooltip>
							}>
							<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
						</StyledBadge1>
					)}
					{!isProtected && isDrmActive && isDrm && (
						<StyledBadge1
							badgeContent={
								<Tooltip title='DRM Protected'>
									<Security color='action' />
								</Tooltip>
							}>
							<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
						</StyledBadge1>
					)}
					{!isProtected && (!isDrmActive || !isDrm) && (
						<StatusChip className={statusChipClasses.bigChip} status={status} name={channel?.name || 'Unknown channel'} />
					)}
				</li>
			</li>
		);
	};

	const columns = [
		{
			title: 'Game',
			sorting: false,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => (
				<>
					{renderSportIcon(row.sport!)}
					<div style={{ display: 'inline-block' }}>
						<CopyText tooltip='Copy Id'>
							<Link component={RouterLink} to={`/games/${encodeURIComponent(row.id)}`} color='secondary'>
								{row.id}
							</Link>
						</CopyText>
						<br />
						<CopyText tooltip='Copy Title'>{row.title!}</CopyText>
					</div>
				</>
			)
		},
		{
			title: 'League',
			field: 'league.name',
			minWidth: 150,
			maxWidth: 150,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): string => {
				const leagueName = row?.league?.name ?? '';
				return leagueName;
			},
			defaultSort: orderBy === 1 ? orderDirection : undefined
		},
		{
			title: `Start time (${getLocalTimezone()})`,
			field: 'startTime',
			align: 'center',
			minWidth: 120,
			maxWidth: 120,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => (
				<>
					<div>
						{row.startTime && dayjs(row.startTime).isValid() ? (
							<>
								<strong>{dayjs(row.startTime).format('DD MMM YYYY')}</strong> {dayjs(row.startTime).format('HH:mm')}
							</>
						) : (
							'Unknown'
						)}
					</div>
					{row.excludeScheduling === true && (
						<div style={{ marginTop: 2 }}>
							<Tooltip title='Excluded from auto-scheduling'>
								<Snooze color='action' style={{ fontSize: '2rem' }} />
							</Tooltip>
						</div>
					)}
				</>
			),
			defaultSort: orderBy === 2 ? orderDirection : undefined
		},
		{
			title: 'Status',
			field: 'status',
			sorting: false,
			align: 'center',
			minWidth: 120,
			maxWidth: 120,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => <StatusChip className={statusChipClasses.smallChip} status={row.status} />
		},
		{
			title: 'Airings',
			field: 'airings',
			sorting: false,
			align: 'center',
			minWidth: 180,
			maxWidth: 180,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): JSX.Element => {
				if (row.airings?.items.length === 0) return <>None</>;

				return (
					<ul className={classes.channelList}>
						{row.airings?.items
							.sort((a, b): number => {
								if (!a.channel?.name || !b.channel?.name) return 0;
								return a.channel?.name > b.channel?.name ? 1 : -1;
							})
							.map((airing): JSX.Element => renderStatusChip(airing))}
					</ul>
				);
			}
		},
		{
			title: 'Max Clips',
			field: 'highlights',
			sorting: false,
			align: 'center',
			minWidth: 100,
			maxWidth: 100,
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Game): string => {
				if (!row.highlights?.items || row.highlights?.items.length === 0) return 'None';

				const highlights = row.highlights?.items;
				let maxClips = 0;
				highlights.forEach((highlight: Highlight) => {
					if (highlight.type !== 'LIVE' || !highlight.periods?.items || highlight.periods?.items.length === 0) return;
					if (highlight.periods?.items.length > maxClips) maxClips = highlight.periods?.items.length;
				});
				return maxClips.toString();
			}
		}
	];

	//prettier-ignore
	const actions = [
        (): { icon: string; tooltip: string } => ({
            icon: 'Options Menu',
            tooltip: 'Options Menu'
        })
    ];

	const options = {
		pageSize,
		search: false,
		sorting: true,
		rowStyle,
		tableLayout: 'fixed',
		actionsColumnIndex: -1,
		actionsCellStyle: {
			margin: 'auto',
			justifyContent: 'center',
			verticalAlign: 'top',
			paddingTop: 10,
			paddingLeft: 33
		},
		emptyRowsWhenPaging: false
	};

	return (
		<MaterialTable
			title='Games'
			localization={tableLocalization}
			columns={columns}
			actions={actions}
			options={options}
			data={games}
			onOrderChange={onOrderChange}
			components={{
				Container: (props): JSX.Element => <Paper {...props} data-testid='games-table' />,
				Pagination: ({ style }): JSX.Element => (
					<TablePagination
						style={style}
						rowsPerPageOptions={[pageSize]}
						rowsPerPage={pageSize}
						count={count}
						page={page}
						onPageChange={(e, page): void => onPageChange(page)}
						labelDisplayedRows={({ from, to }): ReactNode =>
							intl.formatMessage(
								{
									...commonMessages.paginationLabel
								},
								{ from, to }
							)
						}
					/>
				),
				Action: (props): JSX.Element => {
					if (props.action.icon === 'Load More')
						return (
							<Button
								onClick={(event): void => props.action.onClick(event, props.data)}
								color='primary'
								variant='contained'
								style={{ textTransform: 'none' }}
								size='small'>
								Load More
							</Button>
						);

					return <ActionMenu game={props.data} reloadGames={refetch} />;
				}
			}}
		/>
	);
};

type GamesListProps = {
	view: 'ALL' | 'TODAY';
	games: Game[];
	pageSize: number;
	page: number;
	count: number;
	onPageChange: (page: number) => void;
	refetch: Function;
	useDrm: USE_DRM;
};

export default GamesList;
